import { ArrowForward, FormatColorFill, HighlightAlt, Visibility, VisibilityOff } from "@mui/icons-material"
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import makeStyles from "@mui/styles/makeStyles"
import { useContext, useMemo } from "react"
import { connect } from "react-redux"
import { selectConstants, selectTags } from "../../../redux/selectors/firebase"
import ColorInput from "../../../utility/ColorInput"
import { getTagDisplayValue, getUniqueValues } from "../../../utility/constantsTagsUtils"
import { FirebaseContext } from "../../firebase/FirebaseProvider"
import { Constants, ITag, Tags } from "../../graph/StateTags"
import DefaultStyles, { paletteColors } from "../../sheets/conditionalFormatting/DefaultStyles"

const useStyles = makeStyles((theme) => ({
    table: {
        marginTop: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1)
    },
    label: {
        display: "flex",
        gap: theme.spacing(1)
    }
}));

export interface GroupableConstraintValues
{
    data: { [value: string]: { color: string } }
}

interface Props
{
    admin: boolean
    project: string
    constant: string
    tags: Tags
    constants: Constants
    constraint: GroupableConstraintValues
    id: string
    stateFilter?: string[]
    focusGroupValue?: (tag: ITag) => void
    selectGroupOfValue?: (tag: ITag) => void
    toggleVisible?: (tag: ITag) => void
    hiddenGroupMap?: { [key: string]: boolean };
    toggleAllVisible?: (values: any[], allVisible: boolean) => void
}

function GroupableData({ admin, project, tags, constant, constraint, id, constants, stateFilter, focusGroupValue, selectGroupOfValue, toggleVisible, hiddenGroupMap, toggleAllVisible }: Props)
{
    const classes = useStyles();
    const { set } = useContext(FirebaseContext);

    const valueData = constraint?.data ?? {};

    const setColor = (value: string, color: string | null) =>
    {
        value = value.replaceAll(".", "_");
        return set(`constants/${project}/${constant}/constraints/${id}/data/${value}/color/`, color);
    }

    const uniqueValues: any[] = useMemo(() => getUniqueValues(constant, tags), [tags, constant]);

    const getColor = (value: string, index: number) =>
    {
        value = value.replaceAll(".", "_");
        const defaultColor = paletteColors[index % paletteColors.length];
        return value in valueData && valueData[value] && valueData[value].color ? valueData[value].color : defaultColor;
    }

    const filteredTagValues = useMemo(() =>
    {
        if (stateFilter)
        {
            return Array.from(Object.entries(tags).reduce((values, [stateId, stateTags]) =>
            {
                if (stateFilter.includes(stateId))
                {
                    Object.values(stateTags).forEach((tag) =>
                    {
                        if (tag.type === constant)
                        {
                            values.add(tag.value);
                        }
                    });
                }
                return values;
            }, new Set<any>()))
        }

        return null;
    }, [constant, stateFilter, tags]);

    const showActions = focusGroupValue || selectGroupOfValue || toggleVisible;

    const allVisible = hiddenGroupMap ? uniqueValues.every((value) => hiddenGroupMap[String(value)] !== true) : true;

    return <Box className={classes.table}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Value</TableCell>
                    <TableCell>Colour</TableCell>
                    {showActions && <TableCell>Actions {toggleAllVisible && <IconButton size={"small"} onClick={() => toggleAllVisible(uniqueValues, allVisible)}>{allVisible ? <Visibility /> : <VisibilityOff />}</IconButton>}
                    </TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {
                    uniqueValues.map((value, index) =>
                    {
                        return !filteredTagValues || filteredTagValues.includes(value) ? <TableRow key={index}>
                            <TableCell>
                                {getTagDisplayValue({ type: constant, value }, constants)}
                            </TableCell>
                            <TableCell>
                                <Box display={"flex"}>
                                    <ColorInput icon={<FormatColorFill />}
                                        value={getColor(value, index)}
                                        onChange={(color) => setColor(value, color)} />
                                    <DefaultStyles onSelect={(textColor, fillColor) => setColor(value, fillColor)} />
                                </Box>
                            </TableCell>
                            {showActions && <TableCell>
                                {focusGroupValue && <IconButton size={"small"} onClick={() => focusGroupValue({ type: constant, value })}><ArrowForward fontSize="small" /></IconButton>}
                                {selectGroupOfValue && <IconButton size={"small"} onClick={() => selectGroupOfValue({ type: constant, value })}><HighlightAlt fontSize="small" /></IconButton>}
                                {toggleVisible && <IconButton size={"small"} onClick={() => toggleVisible({ type: constant, value })}>{(hiddenGroupMap ?? {})[value] ? <Visibility /> : <VisibilityOff />}</IconButton>}
                            </TableCell>}
                        </TableRow> : null;
                    })
                }
            </TableBody>
        </Table>
    </Box>
}

const mapStateToProps = (store: never) => ({
    tags: selectTags(store) ?? {},
    constants: selectConstants(store) ?? {}
});
export default connect(mapStateToProps, {
})(GroupableData);